/**
 * Connections — the two outside systems intake writes into: the rental
 * system a drafted job lands in, and the calendar it appears in.
 *
 * A section of its own rather than two rows under General, because a
 * Company looking for "the thing we plugged in" looks for a word like
 * this in the sidebar and not under its own name and logo — every
 * product with integrations names them as one place. Each row says what
 * is connected and whether it works, so the state is read here without
 * opening either page; the page is where it is connected, replaced or
 * dropped (`?page=`, committed with `replace`, as every drill is).
 */
import { useSearchParams } from "react-router";
import {
  connectionLine,
  readCalendar,
  readRentalSystem,
} from "../../lib/connections";
import { getCalendar, getRentalSystem } from "../../lib/settings-api";
import { useSettingsSource } from "../../lib/use-settings-source";
import { BackLink, DrillDownRow, DrillList, SectionHeader } from "./section-chrome";
import { CalendarSection } from "./CalendarSection";
import { RentalSystemSection } from "./RentalSystemSection";

const PAGES = [
  {
    id: "rental-system",
    name: "Rental system",
    description: "Where your drafted jobs are written.",
    render: () => <RentalSystemSection />,
  },
  {
    id: "calendar",
    name: "Calendar",
    description: "Where drafted jobs appear in your own calendar.",
    render: () => <CalendarSection />,
  },
];

export function ConnectionsSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The state line per page; the static description stands in until
  // the read lands, and stays if it never does — a failed read here is
  // the page's to report when it is opened, not the list's.
  const rental = useSettingsSource(getRentalSystem).data;
  const calendar = useSettingsSource(getCalendar).data;
  const lines: Record<string, string | undefined> = {
    "rental-system": rental
      ? connectionLine(readRentalSystem(rental.rental_system))
      : undefined,
    calendar: calendar ? connectionLine(readCalendar(calendar)) : undefined,
  };

  const showPage = (page: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (page === null) next.delete("page");
    else next.set("page", page);
    setSearchParams(next, { replace: true });
  };

  const page = PAGES.find((p) => p.id === searchParams.get("page"));
  if (page) {
    return (
      <div>
        <BackLink label="Connections" onClick={() => showPage(null)} />
        {page.render()}
      </div>
    );
  }

  return (
    <section>
      <SectionHeader
        title="Connections"
        description="The rental system your jobs are written into, and the calendar they appear in."
      />
      <DrillList>
        {PAGES.map((p) => (
          <DrillDownRow
            key={p.id}
            name={p.name}
            description={lines[p.id] ?? p.description}
            onOpen={() => showPage(p.id)}
          />
        ))}
      </DrillList>
    </section>
  );
}
